import React from 'react';

import LockIcon from '@mui/icons-material/Lock';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';

import { styles } from '../styles.ts';

/** A fixed PIN is exactly six digits: the API refuses any other when the secret is created. */
export const STATIC_PIN_LENGTH = 6;

interface StaticPinFormProps {
    onSubmit: (pin: string) => Promise<void>;
    error?: string | null;
    isLoading?: boolean;
}

export const StaticPinForm = ({ onSubmit, error, isLoading }: StaticPinFormProps) => {
    const [value, setValue] = React.useState('');
    const [localError, setLocalError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (error) {
            setLocalError(null);
        }
    }, [error]);

    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalError(null);
        // Digits only, and a pasted value is cut to six.
        const input = event.target.value.replace(/\D/g, '').slice(0, STATIC_PIN_LENGTH);
        setValue(input);
    }, []);

    const handleSubmit = React.useCallback(async () => {
        const trimmed = value.trim();

        if (trimmed.length !== STATIC_PIN_LENGTH) {
            setLocalError(`Enter the ${STATIC_PIN_LENGTH}-digit PIN`);
            return;
        }

        setLocalError(null);

        try {
            await onSubmit(trimmed);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'An error occurred. Please try again.';
            setLocalError(message);
        }
    }, [onSubmit, value]);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    const isSubmitDisabled = isLoading || value.length !== STATIC_PIN_LENGTH;
    const displayError = error ?? localError;

    return (
        <Box sx={styles.container}>
            <Box sx={styles.iconWrapper}>
                <LockIcon sx={styles.icon} />
            </Box>

            <Box sx={styles.headerSection}>
                <Typography variant="h6" sx={styles.title}>
                    Enter PIN
                </Typography>
                <Typography variant="body2" sx={styles.subtitle}>
                    This secret is protected with a PIN
                </Typography>
            </Box>

            {displayError && (
                <Alert severity="error" sx={styles.alert}>
                    {displayError}
                </Alert>
            )}

            <TextField
                fullWidth
                type="password"
                placeholder="Enter PIN"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoComplete="one-time-code"
                slotProps={{
                    htmlInput: {
                        inputMode: 'numeric',
                        maxLength: STATIC_PIN_LENGTH,
                        pattern: '[0-9]*',
                        'aria-label': 'PIN input',
                    },
                }}
                sx={styles.passwordInput}
            />

            <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                sx={styles.submitButton}
            >
                {isLoading ? 'Verifying...' : 'Unlock secret'}
            </Button>
        </Box>
    );
};
